/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import GlobalScope from '../../globalScope/GlobalScope';
import {ILogger} from '../../logger/LoggerInterface';
import LoggerFactory from '../../logger/LoggerFactory';
import FeatureEnablement from '../../environment/FeatureEnablement';

const detectionTimeoutInMilliseconds = 1000;

export default class VideoFrameDetector {
  private static _logger: ILogger = LoggerFactory.getLogger('VideoFrameDetector');

  public static isRenderingFrames(videoElement: HTMLVideoElement): Promise<boolean> {
    if (!FeatureEnablement.isRequestVideoFrameCallbackSupported) {
      this._logger.debug('requestVideoFrameCallback not supported, assuming rendering');

      return Promise.resolve(true);
    }

    if (!FeatureEnablement.isRequestAnimationFrameSupported) {
      this._logger.debug('requestAnimationFrame not supported, assuming rendering');

      return Promise.resolve(true);
    }

    // On desktop browsers, the requestVideoFrameCallback vs requestAnimationFrame race
    // produces false negatives after returning from a background tab. The video compositor
    // is inactive while the tab is hidden, so requestVideoFrameCallback does not fire
    // promptly after foregrounding — requestAnimationFrame wins the race even when the
    // stream is healthy. This causes the foreground health check to trigger unnecessary
    // Recovering transitions. On mobile, browsers are more aggressive about pausing or
    // destroying background media pipelines, making frame-level stall detection valuable
    // despite the race risk. Desktop streams rely on the RTC connection monitor's
    // bytes-received stats to detect genuine stalls instead.
    if (!FeatureEnablement.isMobile) {
      this._logger.debug('Skipping frame detection on desktop, assuming rendering');

      return Promise.resolve(true);
    }

    const startTime = Date.now();

    // Detect whether the video element is actively rendering decoded frames by requesting
    // both a video frame callback and an animation frame callback simultaneously.
    // When the stream is healthy, requestVideoFrameCallback fires first because a decoded
    // frame is available for presentation. When the stream is stalled, only
    // requestAnimationFrame fires promptly because it is driven by the display refresh
    // cycle regardless of video state.
    // A false negative (requestAnimationFrame winning despite active rendering) is possible
    // when both callbacks land on the same frame, but is bounded to a single unnecessary
    // restart.
    return new Promise<boolean>(resolve => {
      let resolved = false;
      const timeoutId = GlobalScope.value.setTimeout(() => {
        if (!resolved) {
          resolved = true;
          this._logger.info('Frame detection timed out after [%s] ms, assuming rendering', detectionTimeoutInMilliseconds);

          resolve(true);
        }
      }, detectionTimeoutInMilliseconds);

      videoElement.requestVideoFrameCallback(() => {
        if (!resolved) {
          resolved = true;
          GlobalScope.value.clearTimeout(timeoutId);
          this._logger.debug('Video frame rendered within [%s] ms — stream is healthy', Date.now() - startTime);

          resolve(true);
        }
      });

      // If requestAnimationFrame fires before requestVideoFrameCallback, no video frames
      // are being decoded — the stream is stalled.
      GlobalScope.value.requestAnimationFrame(() => {
        if (!resolved) {
          resolved = true;
          GlobalScope.value.clearTimeout(timeoutId);
          this._logger.debug('No video frame rendered within [%s] ms — stream is stalled', Date.now() - startTime);

          resolve(false);
        }
      });
    });
  }

  constructor() {
    throw new Error('VideoFrameDetector is a static class that may not be instantiated');
  }
}